import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import styles from "../styles/components/ReservationTimer.scss";
// import EventService from "../../services/eventService";

export const ReservationTimer = ({ isCheckout = false, isHeader = false }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  const [timerActive, setTimerActive] = useState(false);
  const [attemptCount, setAttemptCount] = useState(() => {
    const savedAttempts = sessionStorage.getItem("timerAttemptCount");
    return savedAttempts ? parseInt(savedAttempts) : 0;
  });

  const timerIntervalRef = useRef(null);
  const navigate = useNavigate();

  // Función para actualizar el contador de intentos
  const updateAttemptCount = useCallback((count) => {
    setAttemptCount(count);
    if (count > 0) {
      sessionStorage.setItem("timerAttemptCount", count.toString());
    } else {
      sessionStorage.removeItem("timerAttemptCount");
    }
  }, []);

  // Función para detener completamente el temporizador
  const stopTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setTimerActive(false);
    setTimeLeft(null);
    sessionStorage.removeItem("timerAttemptCount");
  }, []);

  // Función para obtener la fecha de expiración más próxima
  const getPriorityExpiration = useCallback((data) => {
    if (!data) return null;

    const expirations = [];
    if (data?.hospedaje?.length > 0) {
      expirations.push(...data.hospedaje.map((item) => item?.fechaExpiracion));
    }
    if (data?.actividades?.length > 0) {
      expirations.push(
        ...data.actividades.map((item) => item?.fechaExpiracion)
      );
    }
    if (data.traslados?.length > 0) {
      expirations.push(...data.traslados.map((item) => item?.fechaExpiracion));
    }

    const validExpirations = expirations
      ?.filter(Boolean)
      ?.map((date) => new Date(date));
    return validExpirations.length > 0
      ? new Date(Math.min(...validExpirations))
      : null;
  }, []);

  // Función para manejar la expiración
  const handleExpiration = useCallback(() => {
    const sessionData = sessionStorage.getItem("datosReserva");
    const urlData = sessionStorage.getItem("datosURL");

    if (!sessionData) {
      stopTimer();
      return;
    }

    try {
      const parsedData = JSON.parse(sessionData);
      const redirectUrl = urlData
        ? JSON.parse(urlData)[0]?.urlHotel?.join("")
        : null;

      if (parsedData?.hospedaje?.length > 0) {
        const updatedData = { ...parsedData, hospedaje: [] };
        sessionStorage.setItem("datosReserva", JSON.stringify(updatedData));
        updateAttemptCount(0);

        Swal.fire({
          title: "Tiempo agotado",
          text: "Se ha agotado el tiempo para tu reserva de hospedaje. Serás redirigido.",
          icon: "warning",
          confirmButtonText: "Entendido",
          willClose: () => {
            if (redirectUrl) {
              stopTimer();
              window.dispatchEvent(new CustomEvent("cartUpdated"));
              window.dispatchEvent(new CustomEvent("sessionStorageUpdated"));
              navigate(redirectUrl);
            }
          },
        });
      } else if (
        parsedData.actividades?.length > 0 ||
        parsedData.traslados?.length > 0
      ) {
        if (attemptCount < 2) {
          const newExpiration = new Date(
            Date.now() + 7 * 60 * 1000
          ).toISOString();
          const updatedData = { ...parsedData };

          if (updatedData.actividades?.length > 0) {
            updatedData.actividades[0].fechaExpiracion = newExpiration;
          } else if (updatedData.traslados?.length > 0) {
            updatedData.traslados[0].fechaExpiracion = newExpiration;
          }

          sessionStorage.setItem("datosReserva", JSON.stringify(updatedData));
          updateAttemptCount(attemptCount + 1);
          setTimeLeft({ minutes: 7, seconds: 0 });

          Swal.fire({
            title: "Tiempo agotado",
            text: `Tendrás ${
              2 - attemptCount
            } intentos más para completar tu reserva.`,
            icon: "warning",
            confirmButtonText: "Entendido",
          });
        } else {
          sessionStorage.removeItem("datosReserva");
          stopTimer();

          Swal.fire({
            title: "Reserva cancelada",
            text: "Se han eliminado los items de tu carrito por inactividad.",
            icon: "error",
            confirmButtonText: "Entendido",
            willClose: () => {
              sessionStorage.clear("datosReserva");
              window.dispatchEvent(new CustomEvent("cartUpdated"));
              window.dispatchEvent(new CustomEvent("sessionStorageUpdated"));
              navigate("/");
            },
          });
        }
      }
    } catch (error) {
      console.error("Error al manejar expiración:", error);
      stopTimer();
    }
  }, [attemptCount, navigate, stopTimer, updateAttemptCount]);

  // Función para limpiar todo (usada desde el botón externo)
  const clearAllTimerData = useCallback(() => {
    stopTimer();

    // Limpiar datos relacionados en sessionStorage
    const sessionData = JSON.parse(sessionStorage.getItem("datosReserva"));
    if (sessionData) {
      try {
        const updatedData = {
          ...sessionData,
          hospedaje: sessionData?.hospedaje?.map((item) => ({
            ...item,
            fechaExpiracion: undefined,
          })),
          actividades: sessionData?.actividades?.map((item) => ({
            ...item,
            fechaExpiracion: undefined,
          })),
          traslados: sessionData?.traslados?.map((item) => ({
            ...item,
            fechaExpiracion: undefined,
          })),
        };

        sessionStorage.setItem("datosReserva", JSON.stringify(updatedData));
      } catch (error) {
        console.error("Error al limpiar datos:", error);
        sessionStorage.removeItem("datosReserva");
      }
    }
    sessionStorage.removeItem("timerAttemptCount");
    window.dispatchEvent(new CustomEvent("sessionStorageUpdated"));
    window.dispatchEvent(new CustomEvent("cartUpdated"));
  }, [stopTimer]);

  const stopReservationTimer = () => {
    clearAllTimerData();
  };

  useEffect(() => {
    // Registrar el evento global al montar
    const handleStopTimer = () => stopReservationTimer();

    window.addEventListener("stopReservationTimerEvent", handleStopTimer);

    return () => {
      // Limpiar al desmontar
      window.removeEventListener("stopReservationTimerEvent", handleStopTimer);
    };
  }, []);

  // Efecto principal
  useEffect(() => {
    const checkSessionStorage = () => {
      try {
        const sessionData = sessionStorage.getItem("datosReserva");
        if (!sessionData) {
          stopTimer();
          return;
        }

        const parsedData = JSON.parse(sessionData);
        const expirationDate = getPriorityExpiration(parsedData);

        if (!expirationDate) {
          stopTimer();
          return;
        }

        const difference = expirationDate - new Date();

        if (difference <= 0) {
          handleExpiration();
          return;
        }

        setTimeLeft({
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
        setTimerActive(true);
      } catch (error) {
        console.error("Error al leer sessionStorage:", error);
        stopTimer();
      }
    };

    checkSessionStorage();

    timerIntervalRef.current = setInterval(checkSessionStorage, 1000);

    const handleStorageChange = () => {
      checkSessionStorage();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("sessionStorageUpdated", handleStorageChange);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("sessionStorageUpdated", handleStorageChange);
    };
  }, [handleExpiration, getPriorityExpiration, stopTimer]);

  if (!timerActive || !timeLeft) return null;

  return (
    <div
      className={
        isHeader
          ? styles.timerContainer + " " + styles.timerHeader
          : `${styles.timerContainer} ${isCheckout ? styles.simpleTimer : ""}`
      }
    >
      <span className={`${styles.timerText}`}>
        {`${timeLeft.minutes.toString().padStart(2, "0")}:${timeLeft.seconds
          .toString()
          .padStart(2, "0")}`}
      </span>
    </div>
  );
};
